import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Column, IconButton, Radio, RadioGroup, Row, Typography } from '@tempmee/react';
import { FiltersDrawer } from '@tempmee/react-blocks';
import { OutlineFilterIcon } from '@tempmee/react-icons';
import { CenteredSpinner, Pagination } from '~/commonComponents';
import { getOnsiteInterviewPath } from '~/components/PermanentJobsV2/utils';
import {
  IGetOnsiteInterviewListViewEntity,
  OnsiteInterviewStatusMapper,
} from '~/domain/PermanentJobs';
import { firePermInterviewListInterviewClicked } from '~/posthogs/events';
import { useOnsiteInterviewTabView } from '../../../JobOverview/components/OnsiteInterviewTabView/useOnsiteInterviewTabView';
import {
  DEFAULT_INTERVIEW_FILTER,
  INTERVIEW_FILTERS,
  POSTING_DETAILS_COPY,
  interviewFilterLabel,
} from '../../postingDetailsRules';
import { PostingInterviewCard } from './PostingInterviewCard';

const FILTER_COPY = POSTING_DETAILS_COPY.filter;

interface IInterviewsTabProps {
  jobPostingId: string;
  /** The posting's office, which an interview invite is sent on behalf of. */
  officeId?: string;
  officeName: string;
  /** Profession badge code for every card, e.g. "RDH". */
  profession?: string;
}

/**
 * The Interviews tab of Posting Details: the posting's onsite interviews earliest first,
 * behind a status filter that defaults to All Upcoming. Each card carries the actions
 * the dashboard's shift lists give an interview (`PostingInterviewCard`). The list request, its sort and
 * the filter's analytics are today's Onsite Interviews tab's, unchanged.
 *
 * The filter is the design system's `FiltersDrawer` (Figma `Molecule/Filters`) with one
 * Status radio group: a choice applies straight away, and Clear goes back to the default
 * and is disabled while the default is selected.
 */
export const InterviewsTab = ({
  jobPostingId,
  officeId,
  officeName,
  profession,
}: IInterviewsTabProps) => {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { data, filters, isLoading, methods, pagination } = useOnsiteInterviewTabView(
    jobPostingId,
    {
      withStatusCount: false,
    },
  );

  const handleFilterChange = (next: string) => {
    const filter = INTERVIEW_FILTERS.find((option) => option.value === next);
    if (filter && filter.value !== filters.status) {
      methods.setStatusFilter(filter.value);
    }
  };

  const handleOpenInterview = (interview: IGetOnsiteInterviewListViewEntity) => {
    firePermInterviewListInterviewClicked({
      postingId: jobPostingId,
      interviewId: interview.id,
      status: OnsiteInterviewStatusMapper.fromJobStatus(interview.status),
    });
    navigate(getOnsiteInterviewPath(interview.id, interview.status));
  };

  const renderList = () => {
    if (data.length === 0) {
      // The list query seeds empty initialData, so react-query never reports it as
      // initially loading: every first load, filter and page change is a fetch over
      // an empty list. Spin through it rather than claim nothing matched.
      return isLoading ? (
        <CenteredSpinner />
      ) : (
        <Typography variant="body1" as="p" className="text-text-secondary">
          {POSTING_DETAILS_COPY.noInterviews}
        </Typography>
      );
    }

    return (
      <Column gap={4} className="w-full">
        {data.map((interview) => (
          <PostingInterviewCard
            key={interview.id}
            interview={interview}
            jobPostingId={jobPostingId}
            officeId={officeId}
            officeName={officeName}
            profession={profession}
            onOpen={handleOpenInterview}
          />
        ))}
      </Column>
    );
  };

  return (
    <Column gap={4} className="w-full" data-cy="posting-interviews">
      <Row align="center" justify="between" px={2}>
        <Typography variant="bodyLarge" as="h2" className="text-text-secondary">
          {interviewFilterLabel(filters.status)}
        </Typography>
        <IconButton
          Icon={<OutlineFilterIcon />}
          size="medium"
          color="secondary"
          // Figma Atom/FilterIcon: Variant=Default, a stroked funnel with no fill.
          appearance="default"
          aria-label={FILTER_COPY.open}
          onClick={() => setIsFilterOpen(true)}
          data-cy="interview-filter-button"
        />
      </Row>
      {renderList()}
      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={methods.setCurrentPage}
      />
      <FiltersDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        heading={FILTER_COPY.title}
        closeLabel={FILTER_COPY.close}
        onClear={() => methods.setStatusFilter(DEFAULT_INTERVIEW_FILTER)}
        clearDisabled={filters.status === DEFAULT_INTERVIEW_FILTER}
        aria-label={FILTER_COPY.open}
        data-cy="interview-filter-drawer"
      >
        <RadioGroup label={FILTER_COPY.status} value={filters.status} onChange={handleFilterChange}>
          {INTERVIEW_FILTERS.map((filter) => (
            <Radio key={filter.value} value={filter.value} label={filter.label} />
          ))}
        </RadioGroup>
      </FiltersDrawer>
    </Column>
  );
};
